import {
  createTheme,
  ThemeProvider as MuiThemeProvider,
} from '@mui/material/styles';
import type { PropsWithChildren } from 'react';
import React, { useMemo } from 'react';
import type { ThemeConfig } from 'antd';
import { ConfigProvider as AntdConfigProvider, Button } from 'antd';
import { StyleProvider } from '@ant-design/cssinjs';
import {
  BREAKPOINT,
  BREAKPOINT_BOUNDARIES_MIN,
} from '../component_library/utils/breakpoints';
import './antd-index.css';
import { useGetTheme } from '../hooks/use-get-theme';
import { GLOBAL_BUTTON_STYLES } from '../component_library/components/Button/button-style-helper';
import 'antd/dist/reset.css';

// MUI comes with some breakpoints defined by default that are different
// than the ones we use. This un-defines those MUI breakpoints, and then defines our
// own custom ones instead.
// https://mui.com/material-ui/customization/theming/#custom-variables
declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xs: false; // removes the `xs` breakpoint
    sm: false;
    md: false;
    lg: false;
    xl: false;
    mobile: true; // adds the `mobile` breakpoint
    tabletMini: true;
    tablet: true;
    desktop: true;
  }
}

// Force preload Ant Styles to avoid FOUC
// This is important for LinkButton,
// where we use Ant design classes for regular components
const PreloadAntStyles = () => (
  <Button
    style={{
      display: 'none',
      height: 0,
      width: 0,
      padding: 0,
      margin: 0,
      border: 'none',
    }}
  />
);

const ThemeProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const defaultTheme = useGetTheme();

  const muiTheme = createTheme({
    breakpoints: {
      values: {
        mobile: BREAKPOINT_BOUNDARIES_MIN[BREAKPOINT.MOBILE],
        tabletMini: BREAKPOINT_BOUNDARIES_MIN[BREAKPOINT.TABLET_MINI],
        tablet: BREAKPOINT_BOUNDARIES_MIN[BREAKPOINT.TABLET],
        desktop: BREAKPOINT_BOUNDARIES_MIN[BREAKPOINT.DESKTOP],
      },
    },
  });

  const antdTheme: ThemeConfig = useMemo(
    () => ({
      // https://github.com/ant-design/ant-design/issues/41926
      hashed: false,
      token: {
        // FONTS
        fontFamily: '"Barlow", sans-serif',

        // COLORS
        colorPrimary: defaultTheme.colors.primary,

        // BREAKPOINTS
        screenXSMin: -1,
        screenXS: -1,
        screenXSMax: -1,
        screenSM: BREAKPOINT_BOUNDARIES_MIN[BREAKPOINT.MOBILE],
        screenSMMin: BREAKPOINT_BOUNDARIES_MIN[BREAKPOINT.MOBILE],
        screenSMMax: BREAKPOINT_BOUNDARIES_MIN[BREAKPOINT.TABLET_MINI] - 1,
        screenMD: BREAKPOINT_BOUNDARIES_MIN[BREAKPOINT.TABLET_MINI],
        screenMDMin: BREAKPOINT_BOUNDARIES_MIN[BREAKPOINT.TABLET_MINI],
        screenMDMax: BREAKPOINT_BOUNDARIES_MIN[BREAKPOINT.TABLET] - 1,
        screenLG: BREAKPOINT_BOUNDARIES_MIN[BREAKPOINT.TABLET],
        screenLGMin: BREAKPOINT_BOUNDARIES_MIN[BREAKPOINT.TABLET],
        screenLGMax: BREAKPOINT_BOUNDARIES_MIN[BREAKPOINT.DESKTOP] - 1,
        screenXL: BREAKPOINT_BOUNDARIES_MIN[BREAKPOINT.DESKTOP],
        screenXLMin: BREAKPOINT_BOUNDARIES_MIN[BREAKPOINT.DESKTOP],

        // MARGINS
        marginXXS: 4,
        marginXS: 8,
        marginSM: 16,
        marginMD: 24,
        marginLG: 32,
        marginXL: 48,
        marginXXL: 64,
      },
      components: {
        Button: {
          ...GLOBAL_BUTTON_STYLES,
          controlHeight: 40,
          controlHeightSM: 32,
          controlHeightLG: 48,
        },
      },
    }),
    [defaultTheme.colors.primary],
  );

  return (
    <AntdConfigProvider theme={antdTheme}>
      <StyleProvider hashPriority="high">
        <MuiThemeProvider theme={muiTheme}>{children}</MuiThemeProvider>
        <PreloadAntStyles />
      </StyleProvider>
    </AntdConfigProvider>
  );
};

export default ThemeProvider;
